import React, { useEffect } from 'react';
import s from './MobileSubscriptionSelection.scss';
import { ClientOffering } from '../../../services/client-offering';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronLeft } from 'wix-ui-icons-common/dist/src';
import classNames from 'classnames';
import { PRODUCT } from '../mobile-constant';
import {
  mobileSendMessageToApp,
  scrollToTop,
} from '../../../services/window-utils';
import { biLogger } from '../../../globals';

interface MobileSubscriptionSelectionProps {
  clientOffering: ClientOffering;
  selectedProduct: number;
  selectedPeriod: number;
  setSelectedPeriod: (index: number) => void;
  onClickOnBack: () => void;
}

export const MobileSubscriptionSelection: React.FunctionComponent<MobileSubscriptionSelectionProps> = (
  props: MobileSubscriptionSelectionProps,
) => {
  const { t } = useTranslation();
  const { selectedProduct, selectedPeriod, setSelectedPeriod } = props;

  const selectedClassName = (index: number) => ({
    [s.isSelected]: index === selectedPeriod,
    [s.isProductLimited]: selectedProduct === PRODUCT.FREE,
    [s.isProductBasic]: selectedProduct === PRODUCT.BASIC,
    [s.isProductPro]: selectedProduct === PRODUCT.PRO,
    [s.isProductUnlimited]: selectedProduct === PRODUCT.UNLIMITED,
  });

  useEffect(() => {
    mobileSendMessageToApp.startPage(2);
    return scrollToTop();
  }, []);

  const selectCycleHandler = (index: number) => {
    const products = props.clientOffering.getProductList(
      t('package.free.name'),
    );
    const package_name = products[selectedProduct].name;
    const packageId = products[selectedProduct].productId || '';
    const selectedCycleElement = products[selectedProduct].cycles[2 - index];
    const cycleName = selectedCycleElement.unit || '';
    const cycleId = `${selectedCycleElement.count}`;
    biLogger.cycleClick({ cycleName, cycleId, packageId, package_name });

    setSelectedPeriod(index);
  };

  const renderPeriod = (
    index: number,
    title: string,
    currency: string,
    amount: number,
    months: number,
    savePercentage?: number,
  ) => (
    <div
      className={classNames(s.period, selectedClassName(index))}
      data-hook="mobile-subscription-period"
      onClick={() => selectCycleHandler(index)}
    >
      <div
        className={classNames(s.periodTitle, selectedClassName(index))}
        data-hook="mobile-subscription-period-title"
      >
        {t(title)}
      </div>
      <div
        className={classNames(s.periodPrice, selectedClassName(index))}
        data-hook="mobile-subscription-period-price"
      >
        {months > 1
          ? t('mobile.subscription.price', { currency, amount, months })
          : t('mobile.subscription.price.aMonth', {
              currency,
              amount,
              months,
            })}
      </div>
      {savePercentage && (
        <div
          className={classNames(s.periodSaved, selectedClassName(index))}
          data-hook="mobile-subscription-period-saved"
        >
          {t('packageCycleModal.save', { savePercentage })}
        </div>
      )}
    </div>
  );

  const currency = props.clientOffering.getCurrency();
  const cycles = props.clientOffering.products[selectedProduct].cycles;

  return (
    <div className={s.strip} data-hook="mobile-subscription">
      <div className={s.title} data-hook="mobile-subscription-title">
        <div
          className={s.backButton}
          data-hook="mobile-subscription-backButton"
          onClick={props.onClickOnBack}
        >
          <ChevronLeft style={{ height: '6.4vw', width: '6.4vw' }} />
        </div>
        {t('packageCycleModal.title')}
      </div>
      <div className={s.periodsList}>
        {renderPeriod(
          0,
          'cycle.2years',
          currency,
          cycles[2].price,
          cycles[2].monthsPerCycle,
          cycles[2].savePercentage,
        )}
        {renderPeriod(
          1,
          'cycle.yearly',
          currency,
          cycles[1].price,
          cycles[1].monthsPerCycle,
          cycles[1].savePercentage,
        )}
        {renderPeriod(
          2,
          'cycle.monthly',
          currency,
          cycles[0].price,
          cycles[0].monthsPerCycle,
          cycles[0].savePercentage,
        )}
      </div>
    </div>
  );
};

export default MobileSubscriptionSelection;
